import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronRight, Circle, Loader2, Route } from 'lucide-react';
import { CURSOR_CHAT, PORTAL_CHAT } from '../arkorbit-ui/portalTheme';
import {
  buildThinkingStages,
  pickActiveStageIndex,
  type ThinkingContext,
} from './arkoThinkingStages';

export interface ArkoThinkingProgressProps {
  context: ThinkingContext;
  variant?: 'portal' | 'cursor';
  compact?: boolean;
  defaultExpanded?: boolean;
}

type StepStatus = 'done' | 'active' | 'pending';

export const ArkoThinkingProgress: React.FC<ArkoThinkingProgressProps> = ({
  context,
  variant = 'portal',
  compact = false,
  defaultExpanded = true,
}) => {
  const stages = useMemo(() => buildThinkingStages(context), [context]);
  const startedAtRef = React.useRef(Date.now());
  const [tick, setTick] = useState(() => Date.now());
  const activeIndex = pickActiveStageIndex(stages.length, tick - startedAtRef.current);

  useEffect(() => {
    startedAtRef.current = Date.now();
    setTick(Date.now());
  }, [context.flow, context.chatInput, context.intent]);

  useEffect(() => {
    const timer = window.setInterval(() => setTick(Date.now()), 400);
    return () => window.clearInterval(timer);
  }, []);

  const bubbleClass =
    variant === 'cursor' ? CURSOR_CHAT.typingBubble : PORTAL_CHAT.typingBubble;

  const statusFor = (index: number): StepStatus => {
    if (index < activeIndex) return 'done';
    if (index === activeIndex) return 'active';
    return 'pending';
  };

  const activeLabel = stages[activeIndex]?.label ?? 'ARKO denkt nach…';

  return (
    <details
      className={`${bubbleClass} ${compact ? 'p-2' : 'p-3'} max-w-[min(100%,28rem)] group`}
      open={defaultExpanded}
    >
      <summary className="list-none cursor-pointer select-none flex items-center gap-1.5">
        <ChevronRight
          className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90 shrink-0"
          aria-hidden
        />
        <Route className="w-3 h-3 text-violet-400/80 shrink-0" aria-hidden />
        <span className="text-[11px] font-medium text-slate-300">Denkprozess</span>
        <span className="text-[10px] text-slate-500 truncate">· {activeLabel}</span>
      </summary>
      <ol
        className="mt-2 space-y-1 border-l border-white/[0.08] ml-1 pl-2.5"
        aria-live="polite"
        aria-label="ARKO Denkprozess"
      >
        {stages.map((stage, index) => {
          const status = statusFor(index);
          return (
            <li
              key={stage.id}
              className={`flex items-start gap-1.5 text-[10px] leading-snug ${
                status === 'active'
                  ? 'text-violet-200/90'
                  : status === 'done'
                    ? 'text-slate-500'
                    : 'text-slate-600'
              }`}
            >
              <span className="mt-0.5 shrink-0" aria-hidden>
                {status === 'done' ? (
                  <CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
                ) : status === 'active' ? (
                  <Loader2 className="w-3 h-3 animate-spin text-violet-300" />
                ) : (
                  <Circle className="w-3 h-3 text-slate-600" />
                )}
              </span>
              <span>{stage.label}</span>
            </li>
          );
        })}
      </ol>
    </details>
  );
};
